import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { FaCircleCheck } from "react-icons/fa6";
import { MdCancel, MdWarning } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import check from "../assets/checkstatus.png";
import Notcheck from "../assets/cross.png";
import arrow from "../assets/right-arrow.png";
import { buttonloginstyle, buttonloginstyle2 } from "../help/Utils";
import {
  EligibilityFormQuestion,
  fetchVisaQuestions,
  VisaType,
  createApplication,
  submitEligibilityResponses,
} from "../API/visaEligibilityAPI";
import { getApplication, downloadEligibilityReportPdf } from "../API/applicationAPI";
import BeneficiaryModal from "../components/BeneficiaryModal";
import { getCalendlyLink } from "../API/calendlyAPI";
import { getUserProfile } from "../API/profileAPI";
import { isSignedIn } from "../utils/authStorage";
import {
  computeEligibilityResultStatus,
  getEligibilityConcerns,
} from "../utils/eligibilityResult";
import {
  ELIGIBILITY_RESULT_CONTENT,
  ELIGIBILITY_ACKNOWLEDGEMENT_VERSION,
} from "../data/eligibilityResultContent";
import EligibilityAgreement from "../components/eligibility/EligibilityAgreement";
import { recordEligibilityAcknowledgement } from "../API/applicationAPI";
import { toast } from "react-toastify";

const AnswerReview = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<EligibilityFormQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const reportRef = useRef<HTMLDivElement>(null);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const [visaType, setVisaType] = useState("");
  const [parsedAnswers, setParsedAnswers] = useState<{
    [key: EligibilityFormQuestion["id"]]: number | number[];
  }>({});
  const [showBeneficiaryModal, setShowBeneficiaryModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [agreed, setAgreed] = useState(false);

  const getVisaTypeFromEligibilityKey = (key: string): VisaType | null => {
    const parts = key.split(":");
    const maybeVisaType = parts[0] === "eligibility" ? parts[1] : parts[0];
    return maybeVisaType ? (maybeVisaType as VisaType) : null;
  };

  const getEligibilitySessionKey = (): string | null => {
    try {
      const answersRaw = JSON.parse(
        localStorage.getItem("visaEligibilityResponses") || "{}"
      );
      return Object.keys(answersRaw)[0] || null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    setIsLoggedIn(isSignedIn());
  }, []);

  /**
   * get the questions for the visa type
   */
  const getConfig = useCallback(async (visaType: VisaType) => {
    const response = await fetchVisaQuestions(visaType);
    setQuestions(response);
  }, []);

  /**
   * Resolve the beneficiary name from localStorage (or the user's profile when
   * the application is for "self"), mirroring handleStartApplication.
   */
  const resolveBeneficiaryName = async (): Promise<string> => {
    let name = localStorage.getItem("beneficiaryName");
    if (!name && localStorage.getItem("beneficiaryType") === "self") {
      name = (await getUserProfile())?.data?.data?.fullName ?? null;
    }
    return name || "";
  };

  /**
   * Ensure an application exists for this eligibility result and return its id.
   * Reuses a valid `currentApplicationId` when present; otherwise creates the
   * application and submits the eligibility responses. Does NOT clear the
   * eligibility localStorage (the user stays on this page) — that cleanup
   * happens only when the user actually starts the application.
   */
  const ensureApplication = async (beneficiaryName: string): Promise<number> => {
    const existingAppId = localStorage.getItem("currentApplicationId");
    const existingQualified = localStorage.getItem("isQualified");

    if (existingAppId && existingQualified) {
      try {
        const existingApp = await getApplication(parseInt(existingAppId));
        if (existingApp && existingApp.visaType === visaType) {
          return parseInt(existingAppId);
        }
      } catch {
        // fall through to create a fresh application
      }
      localStorage.removeItem("currentApplicationId");
      localStorage.removeItem("isQualified");
    }

    const application = await createApplication(
      beneficiaryName,
      visaType as VisaType,
      { idempotencyKey: getEligibilitySessionKey() }
    );
    if (application.stage !== "eligibility_check") {
      localStorage.setItem("currentApplicationId", application.id.toString());
      localStorage.setItem("isQualified", String(application.isQualified));
      return application.id;
    }
    const apiResponses = transformAnswersToAPIFormat(parsedAnswers, questions);
    const result = await submitEligibilityResponses(application.id, apiResponses);
    localStorage.setItem("currentApplicationId", application.id.toString());
    localStorage.setItem("isQualified", String(result.isQualified));
    return application.id;
  };

  /**
   * Download the eligibility report. The PDF is generated server-side by
   * lgv-pdf-filler, so we must have an application first: create or reuse one,
   * then download the streamed PDF blob. No client-side PDF generation.
   */
  const handleDownloadPDF = async () => {
    // Task 149: no readable token. This marker decides where to send the
    // applicant; the API still authorises the download from the cookie.
    if (!isSignedIn()) {
      navigate("/login");
      return;
    }
    try {
      setIsDownloadingReport(true);
      const beneficiaryName = await resolveBeneficiaryName();
      const applicationId = await ensureApplication(beneficiaryName);
      await downloadEligibilityReportPdf(applicationId);
    } catch (error: any) {
      console.error("Error downloading eligibility report:", error);
      toast.error(
        error?.message ||
          "There was an error generating your eligibility report. Please try again."
      );
    } finally {
      setIsDownloadingReport(false);
    }
  };

  /**
    * the answers are stored in the local storage in the following format:
    * /* {
     *   "N-400:1758997735676": {
    //     "3": 5,
    //     "4": 7,
    //     "10": 20,
    //     "11": 22,
    //     "12": 23,
    //     "13": 26,
    //     "14": 28,
    //     "15": 33,
    //     "16": 35,
    //     "17": 38,
    //     "18": 40
    //   }
       * } */

  /**
   * POST
   * user/user-responses
   * Submit multiple eligibility question responses
   * Parameters
   * No parameters
   * Request body
   * [
   *   {
   *     "questionId": 3,
   *     "optionId": 5,
   *     "visaType": "N-400",
   *     "answerText": "Yes",
   *     "explanation": "Proceed to Step 2",
   *     "isReview": false
   *   }
   * ]
   */

  useEffect(() => {
    const answersRaw = JSON.parse(
      localStorage.getItem("visaEligibilityResponses") || "{}"
    );
    const eligibilityKey = Object.keys(answersRaw)[0];
    const storedVisaType = eligibilityKey
      ? getVisaTypeFromEligibilityKey(eligibilityKey)
      : null;
    if (!storedVisaType || !answersRaw[eligibilityKey]) {
      navigate("/");
      return;
    }
    getConfig(storedVisaType)
      .catch((error) => {
        console.error("Failed to load eligibility questions:", error);
        navigate("/");
      })
      .finally(() => {
        setLoading(false);
      });
    setParsedAnswers(answersRaw[eligibilityKey]);
    setVisaType(storedVisaType);
  }, [getConfig, navigate]);

  /**
   * pass = all contributing answers correct
   * warn = only incorrectAsWarning concerns (still eligible to continue)
   * fail = at least one hard-fail incorrect answer
   */
  const resultStatus = useMemo(
    () => computeEligibilityResultStatus(questions, parsedAnswers),
    [questions, parsedAnswers]
  );
  const concerns = useMemo(
    () => getEligibilityConcerns(questions, parsedAnswers),
    [questions, parsedAnswers]
  );
  /** Pass and warn both continue with the primary application CTA */
  const canContinueAsEligible = resultStatus !== "fail";

  /** Client-approved heading, paragraphs and acknowledgements for this tier. */
  const resultContent = ELIGIBILITY_RESULT_CONTENT[resultStatus];

  /**
   * One agreement to the tier's acknowledgements (client worklist #79). It is
   * reset whenever the tier changes, so agreement to one set of wording never
   * carries over to a different one.
   */
  useEffect(() => {
    setAgreed(false);
  }, [resultContent]);

  /**
   * Guests create an account from here; a logged-in applicant gets the same
   * acknowledgements without being pushed back through signup.
   */
  const ctaLabel = isLoggedIn
    ? "Agree & Continue"
    : "Create Account & Continue";

  /**
   * Transform localStorage answers to API format
   * Converts: { questionId: optionId }
   * To: [{ questionId, optionId, answerText, explanation, isReview }]
   */
  const transformAnswersToAPIFormat = (
    parsedAnswers: { [key: number]: number | number[] },
    questions: EligibilityFormQuestion[]
  ) => {
    const responses: Array<{
      questionId: number;
      optionId: number;
      answerText: string;
      explanation: string | null;
      isReview: boolean;
    }> = [];

    Object.entries(parsedAnswers).forEach(([questionIdStr, answerId]) => {
      const questionId = parseInt(questionIdStr);
      const question = questions.find((q) => q.id === questionId);

      if (!question) return;

      if (question.multiSelect && Array.isArray(answerId)) {
        // Multi-select: each selected option becomes a separate response
        answerId.forEach((optId) => {
          const option = question.options?.find((o) => o.id === optId);
          if (option) {
            responses.push({
              questionId,
              optionId: optId,
              answerText: option.text,
              explanation: option.explanation || null,
              isReview: false,
            });
          }
        });
      } else if (typeof answerId === "number") {
        // Single-select
        const option = question.options?.find((o) => o.id === answerId);
        if (option) {
          responses.push({
            questionId,
            optionId: answerId,
            answerText: option.text,
            explanation: option.explanation || null,
            isReview: false,
          });
        }
      }
    });

    return responses;
  };

  const handleStartApplication = async () => {
    if (!isSignedIn()) {
      // Guests create an account here. The eligibility session stays in
      // localStorage, and sign-up / email verification both resume to
      // /answer-review, so the applicant lands back on this result.
      navigate("/sign-up");
      return;
    }
    const storedBeneficiaryName = await resolveBeneficiaryName();
    handleBeneficiarySubmit(storedBeneficiaryName);
  };

  const handleBeneficiarySubmit = async (beneficiaryName: string) => {
    setShowBeneficiaryModal(false);
    setIsSubmitting(true);

    try {
      // Create or reuse the application and submit responses.
      const applicationId = await ensureApplication(beneficiaryName);

      // Record the agreement they just gave (client worklist #79). It used to
      // exist only in this page's state. If recording fails, carry on: the
      // payment page checks for it and asks again, so nobody pays without it.
      try {
        await recordEligibilityAcknowledgement(
          applicationId,
          ELIGIBILITY_ACKNOWLEDGEMENT_VERSION
        );
      } catch (ackError) {
        console.error("Could not record eligibility agreement:", ackError);
      }

      // Clean up localStorage now that the user is starting the application.
      localStorage.removeItem("visaEligibilityResponses");
      localStorage.removeItem("latestVisaType");
      localStorage.removeItem("userQuestionHistoryStack");
      localStorage.removeItem("beneficiaryName");
      localStorage.removeItem("beneficiaryType");

      navigate("/subscription-package", {
        state: { visaType, applicationId },
      });
    } catch (error: any) {
      console.error("Error creating application:", error);
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "There was an error processing your application. Please try again or contact support."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConnectExpert = async () => {
    // isSubmitting to true
    setIsSubmitting(true);
    await handleCalendlySubmit();
    window.open(getCalendlyLink(), "_blank");
  };

  const handleCalendlySubmit = async () => {
    // beneficiary type check
    const beneficiaryType = localStorage.getItem("beneficiaryType");
    // Get user details from localStorage if available
    const userDetails = {
      visaType,
      eligibilityStatus: resultContent.heading,
      beneficiaryName:
        beneficiaryType === "self"
          ? (await getUserProfile())?.data?.data?.fullName
          : "",
    };

    // Create or reuse the application. This prevents a duplicate case if the
    // user already downloaded the server-generated eligibility report first.
    await ensureApplication(userDetails.beneficiaryName);

    // Clear the local storage
    localStorage.removeItem("visaEligibilityResponses");
    localStorage.removeItem("latestVisaType");
    localStorage.removeItem("userQuestionHistoryStack");
    localStorage.removeItem("beneficiaryName");
    localStorage.removeItem("beneficiaryType");
    localStorage.removeItem("currentApplicationId");
    localStorage.removeItem("isQualified");

    // Navigate to dashboard
    navigate("/dashboard");
  };

  if (loading) {
    return (
      <div className="text-center font-bold py-80 text-2xl text-[#1e2b5e] font-georgia px-4">
        Loading your eligibility summary...
      </div>
    );
  }

  return (
    <div className="relative login_main body_padding pb-[100px]">
      <div
        ref={reportRef}
        style={{
          background: "white",
          borderRadius: "12px",
          border: "1px solid #e5e7eb",
          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
          padding: "35px",
          width: "100%",
          maxWidth: "650px",
          margin: "0 auto 24px",
        }}
      >
        <div>
          <div
            style={{
              width: "88px",
              height: "88px",
              margin: "0 auto 20px",
              borderRadius: "9999px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background:
                resultStatus === "pass"
                  ? "linear-gradient(180deg, #E8F8F0 0%, #F3FBF7 100%)"
                  : resultStatus === "warn"
                    ? "linear-gradient(180deg, #FFF6E5 0%, #FFFBF2 100%)"
                    : "linear-gradient(180deg, #FDECEC 0%, #FFF7F7 100%)",
              border:
                resultStatus === "pass"
                  ? "1px solid #C6EBD6"
                  : resultStatus === "warn"
                    ? "1px solid #F0D4A8"
                    : "1px solid #F5C6C6",
            }}
          >
            {resultStatus === "pass" ? (
              <img
                src={check}
                alt="Preliminary eligibility result"
                style={{ width: "56px", height: "56px", borderRadius: "9999px" }}
              />
            ) : resultStatus === "warn" ? (
              <MdWarning
                aria-label="Eligible with concerns"
                style={{ color: "#CA850B", fontSize: "48px" }}
              />
            ) : (
              <img
                src={Notcheck}
                alt="Eligibility result"
                style={{ width: "56px", height: "56px", borderRadius: "9999px" }}
              />
            )}
          </div>
          <h2
            style={{
              fontSize: "28px",
              textAlign: "center",
              color:
                resultStatus === "fail"
                  ? "#FF272A"
                  : resultStatus === "warn"
                    ? "#9A6212"
                    : "#1B365D",
              marginBottom: "12px",
              fontFamily: "Georgia",
            }}
          >
            {resultContent.heading}
          </h2>

          <p
            style={{
              color: "#1B365D",
              textAlign: "center",
              marginBottom: resultStatus === "warn" ? "16px" : "24px",
              maxWidth: "520px",
              marginLeft: "auto",
              marginRight: "auto",
              lineHeight: 1.5,
            }}
          >
            {resultContent.assessment}
          </p>

          <p
            style={{
              color: "#4B5563",
              textAlign: "center",
              marginBottom: "24px",
              maxWidth: "520px",
              marginLeft: "auto",
              marginRight: "auto",
              lineHeight: 1.5,
              fontSize: "14px",
            }}
          >
            {resultContent.disclosure}
          </p>

          {resultStatus !== "pass" && concerns.length > 0 && (
            <div
              style={{
                backgroundColor: "#FFF8EB",
                border: "1px solid #F0D4A8",
                borderRadius: "12px",
                padding: "16px 20px",
                marginBottom: "24px",
                maxWidth: "520px",
                marginLeft: "auto",
                marginRight: "auto",
              }}
            >
              <p
                style={{
                  fontWeight: 700,
                  color: "#9A6212",
                  marginBottom: "8px",
                  fontSize: "14px",
                }}
              >
                Areas of concern
              </p>
              <ul
                style={{
                  margin: 0,
                  paddingLeft: "20px",
                  color: "#1B365D",
                  fontSize: "14px",
                  lineHeight: 1.5,
                }}
              >
                {concerns.map((concern) => (
                  <li key={concern} style={{ marginBottom: "4px" }}>
                    {concern}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div
            style={{
              backgroundColor: "#F9FAFB",
              padding: "24px",
              borderRadius: "12px",
              marginBottom: "25px",
            }}
          >
            <h5
              style={{
                fontWeight: "bold",
                fontSize: "24px",
                lineHeight: "40px",
                marginBottom: "16px",
              }}
            >
              Your Eligibility Summary — {visaType}
            </h5>

            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "14px",
              }}
            >
              <tbody>
                {questions
                  .filter((q) => q.contributesToEligibility)
                  // Filter unanswered questions out
                  .filter((q) => parsedAnswers[q.id])
                  .map((q) => {
                    const answerId = parsedAnswers[q.id];

                    // Handle multi-select vs single-select answers
                    let selectedOptions: any[] = [];
                    let allCorrect = true;

                    if (q.multiSelect && Array.isArray(answerId)) {
                      // Multi-select: get all selected options
                      selectedOptions =
                        q.options?.filter((o) => answerId.includes(o.id)) || [];
                      allCorrect =
                        selectedOptions.length > 0 &&
                        selectedOptions.every((opt) => opt.isCorrect === true);
                    } else if (answerId) {
                      // Single-select: get the single selected option
                      const answer = q.options?.find((o) => o.id === answerId);
                      if (answer) {
                        selectedOptions = [answer];
                        allCorrect = answer.isCorrect === true;
                      }
                    }

                    const rowTone: "pass" | "warn" | "fail" = allCorrect
                      ? "pass"
                      : q.incorrectAsWarning
                        ? "warn"
                        : "fail";

                    return (
                      <tr key={q.id}>
                        <td style={{ padding: "10px", verticalAlign: "top" }}>
                          {rowTone === "pass" ? (
                            <FaCircleCheck
                              style={{ color: "#2F9E66", fontSize: "18px" }}
                            />
                          ) : rowTone === "warn" ? (
                            <MdWarning
                              style={{ color: "#CA850B", fontSize: "20px" }}
                            />
                          ) : (
                            <MdCancel
                              style={{ color: "#FF272A", fontSize: "20px" }}
                            />
                          )}
                        </td>
                        <td style={{ padding: "10px" }}>
                          <div style={{ fontWeight: "600" }}>
                            {q.questionText}
                          </div>
                          {selectedOptions.length > 0 ? (
                            selectedOptions.map((option, index) => (
                              <div key={option.id || index}>
                                — {option.text}
                              </div>
                            ))
                          ) : (
                            <div
                              style={{ color: "#6B7280", fontStyle: "italic" }}
                            >
                              No answer provided
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>

        {/* ❌ EXCLUDED from PDF */}
        <h5
          style={{
            fontWeight: "bold",
            fontSize: "24px",
            lineHeight: "40px",
            marginBottom: "16px",
          }}
        >
          Recommended Next Steps
        </h5>

        <div style={{ display: "flex", marginBottom: "20px" }}>
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              backgroundColor: "#F3F4F6",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginRight: "16px",
              marginTop: "4px",
            }}
          >
            1
          </div>
          <div>
            <h6 style={{ fontWeight: "bold", fontSize: "16px" }}>
              Start Your {visaType} Application
            </h6>
            <p style={{ fontSize: "14px" }}>
              Begin your citizenship journey with our guided application
              process.
            </p>
          </div>
        </div>

        <div style={{ display: "flex", marginBottom: "20px" }}>
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              backgroundColor: "#F3F4F6",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginRight: "16px",
              marginTop: "4px",
            }}
          >
            2
          </div>
          <div>
            <h6 style={{ fontWeight: "bold", fontSize: "16px" }}>
              Gather Required Documents
            </h6>
            <p style={{ fontSize: "14px" }}>
              We'll provide a personalized checklist for your case.
            </p>
          </div>
        </div>

        <div style={{ width: "100%", maxWidth: "90%", margin: "0 auto" }}>
          {/* Status-specific acknowledgements, agreed to once. */}
          <EligibilityAgreement
            tier={resultStatus}
            checked={agreed}
            onChange={setAgreed}
            heading={isLoggedIn ? "Before You Continue" : "Before Creating Your Account"}
          />

          <button
            type="button"
            disabled={!agreed}
            style={{
              ...buttonloginstyle,
              width: "100%",
              marginTop: "20px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              opacity: agreed ? 1 : 0.5,
              cursor: agreed ? "pointer" : "not-allowed",
            }}
            onClick={handleStartApplication}
          >
            {ctaLabel}
            <img src={arrow} alt="arrow" style={{ marginLeft: "12px" }} />
          </button>

          {/* Yellow and red keep the attorney recommendation without being
              blocked from preparing their forms. */}
          {!canContinueAsEligible || resultStatus === "warn" ? (
            <button
              type="button"
              style={{
                ...buttonloginstyle2,
                width: "100%",
                marginTop: "12px",
              }}
              onClick={handleConnectExpert}
            >
              Contact an Attorney
            </button>
          ) : null}

          <button
            type="button"
            disabled={isDownloadingReport}
            style={{
              ...buttonloginstyle2,
              width: "100%",
              marginTop: "20px",
              opacity: isDownloadingReport ? 0.7 : 1,
              cursor: isDownloadingReport ? "not-allowed" : "pointer",
            }}
            onClick={handleDownloadPDF}
          >
            {isDownloadingReport
              ? "Preparing Report..."
              : "Download Eligibility Report"}
          </button>
        </div>
      </div>

      <div style={{ marginTop: "24px", paddingTop: "20px" }}>
        <p style={{ textAlign: "center", fontWeight: "600" }}>
          Questions about your results?{" "}
          <a
            href="mailto:support@letsgetvisa.com"
            style={{ color: "#E76F51", fontWeight: "bold" }}
          >
            Contact our support team
          </a>
        </p>
      </div>

      {/* Beneficiary Modal */}
      <BeneficiaryModal
        show={showBeneficiaryModal}
        onClose={() => setShowBeneficiaryModal(false)}
        onSubmit={handleBeneficiarySubmit}
        userFullName={localStorage.getItem("email")?.split("@")[0] || ""}
      />

      {/* Loading overlay while submitting */}
      {isSubmitting && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 shadow-lg text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#F4A261] mx-auto mb-4"></div>
            <p className="text-xl font-semibold text-[#1B365D]">
              Creating your application...
            </p>
            <p className="text-sm text-gray-600 mt-2">
              Please wait while we save your information
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnswerReview;
